import { JsonForms } from '@jsonforms/react';
import type { JsonSchema, UISchemaElement } from '@jsonforms/core';
import { materialCells, materialRenderers } from '@jsonforms/material-renderers';
import { customRenderers } from '@/components/forms/renderers';
import { JsonFormsThemeProvider } from '@/components/forms/JsonFormsTheme';
import { nexoJsonFormsAjv } from '@/lib/jsonFormsAjv';
import { useJsonFormsTranslation } from '@/hooks/useJsonFormsTranslation';

/** A command's declared input as a form: generated per entity in `{Entity}CommandInputs.ts`. */
export interface CommandInputFormSpec {
  schema: JsonSchema;
  uischema: UISchemaElement;
}

interface CommandInputFormProps {
  /** Entity name, for labels and validation messages (e.g. `Order`). */
  entity: string;
  form: CommandInputFormSpec;
  data: Record<string, unknown>;
  /** The values and whether the form's schema accepts them (a required input left empty does not). */
  onChange: (data: Record<string, unknown>, valid: boolean) => void;
  /** Shows the values without letting them change, e.g. while a bulk run waits on its warnings. */
  readonly?: boolean;
}

const renderers = [...materialRenderers, ...customRenderers];

/**
 * The form a command's input is asked for with (docs/15-designspecs/Bulk-Edit-And-Actions.md §8.3) — the same in the
 * Actions menu, for one record, and in the list's bulk run, filled once for every selected row (D6).
 */
export function CommandInputForm({ entity, form, data, onChange, readonly = false }: CommandInputFormProps) {
  const i18n = useJsonFormsTranslation(entity);
  return (
    <JsonFormsThemeProvider density="compact" variant="standard">
      <JsonForms
        schema={form.schema}
        uischema={form.uischema}
        data={data}
        readonly={readonly}
        renderers={renderers}
        cells={materialCells}
        ajv={nexoJsonFormsAjv}
        i18n={i18n}
        onChange={({ data: next, errors }) => onChange((next ?? {}) as Record<string, unknown>, (errors ?? []).length === 0)}
      />
    </JsonFormsThemeProvider>
  );
}
